import { BottomTabScreenProps, createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import {
  CompositeScreenProps,
  NavigationProp,
  NavigatorScreenParams,
  ParamListBase,
  TabActions,
} from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Box, Circle, Flex, Pressable, Spinner } from 'native-base';
import React, { lazy, ReactNode, Suspense } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useShallow } from 'zustand/react/shallow';

import Menu from 'app/components/icons/Menu';
import QRTicket from 'app/components/icons/QRTicket';
import RemoteSVG from 'app/components/RemoteSVG';
import useGuest from 'app/hooks/useGuest';
import useProduct from 'app/hooks/useProduct';
import useStore from 'app/hooks/useStore';
import type { AttractionTicketsStackParamList } from 'app/navigation/AttractionTicketsStackNavigator';
import { getRouteParam } from 'app/navigation/navigationHelper';
import type { ProductStackParamList } from 'app/navigation/ProductStackNavigator';
import ProductStackNavigator from 'app/navigation/ProductStackNavigator';
import type { RootStackParamList } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import { useMessaging } from 'app/providers/MessagingProvider';
import MenuScreen from 'app/screens/MenuScreen';
import Analytics, { Events } from 'app/services/Analytics';
import { tID } from 'app/services/TestHelper';

export type ProductTabScreenProps = BottomTabScreenProps<RootStackParamList, 'Tabs'>;

export type ProductTabParamList = {
  ProductStack: NavigatorScreenParams<ProductStackParamList> | undefined;
  AttractionTicketsStack: NavigatorScreenParams<AttractionTicketsStackParamList>;
  Menu: undefined;
};

export type ProductTabScreenCompositeProps = CompositeScreenProps<
  BottomTabScreenProps<ProductTabParamList, 'ProductStack'>,
  NativeStackScreenProps<RootStackParamList>
>;

const AttractionTicketsStackNavigator = lazy(
  () => import('app/navigation/AttractionTicketsStackNavigator')
);

// Stable wrapper so the lazy Suspense boundary is at the tab level, not the root NavigationContainer
function AttractionTicketsTab() {
  return (
    <Suspense fallback={<Box flex={1} />}>
      <AttractionTicketsStackNavigator />
    </Suspense>
  );
}

/**
 * A bottom tab navigator displays tab buttons on the bottom of the display to switch screens.
 * https://reactnavigation.org/docs/bottom-tab-navigator
 */
const BottomTab = createBottomTabNavigator<ProductTabParamList>();
const ICON_SIZE = 26;
const TAB_BAR_PADDING = 10;

export default function ProductTabNavigator({ navigation, route }: ProductTabScreenProps) {
  const { storeProductId, storeLastTab } = useStore(
    useShallow((s) => ({ storeProductId: s.productId, storeLastTab: s.lastTab }))
  );
  const productContentKey = getRouteParam(route, 'productId') ?? storeProductId ?? '';

  const { t } = useI18n();
  const { productContent } = useProduct({ productContentKey });
  const { activeEntitlement, hasActiveEntitlementFor } = useGuest();
  const { bottom } = useSafeAreaInsets();
  const routeParams = { productId: productContentKey };
  const entitlement = hasActiveEntitlementFor(productContentKey) ? activeEntitlement : undefined;
  const { unreadCount } = useMessaging();

  return (
    <BottomTab.Navigator
      initialRouteName={storeLastTab ?? 'ProductStack'}
      screenListeners={({ route: screenRoute }) => ({
        focus: () => {
          const name = screenRoute.name as keyof ProductTabParamList;
          if (
            (name === 'ProductStack' || name === 'AttractionTicketsStack') &&
            useStore.getState().lastTab !== name
          ) {
            useStore.getState().setLastTab(name);
          }
        },
      })}
      screenOptions={{
        headerShadowVisible: true,
        tabBarStyle: {
          height: 70 + bottom,
          paddingTop: TAB_BAR_PADDING,
          paddingBottom: TAB_BAR_PADDING + bottom,
          elevation: 24,
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 0,
          },
          shadowOpacity: 0.25,
          shadowRadius: 10,
        },
        tabBarLabelStyle: {
          paddingTop: 5,
        },
        tabBarLabelPosition: 'below-icon',
        tabBarInactiveTintColor: '#373737',
      }}
    >
      <BottomTab.Screen
        component={ProductStackNavigator}
        initialParams={{
          screen: 'Product',
          params: { productId: productContentKey },
        }}
        name="ProductStack"
        options={({ navigation: $navigation }) => ({
          title: t<string>(entitlement ? 'app_product_tab_home_auth' : 'app_product_tab_home'),
          tabBarButtonTestID: tID('TabDestination'),
          tabBarButton: ({ children }) => (
            <TabBarButton
              analyticsEvent={Events.NavProductHome}
              navigation={$navigation}
              routeParams={routeParams}
              screen="ProductStack"
              testID={tID('TabDestination')}
            >
              {children}
            </TabBarButton>
          ),
          headerShown: false,
          tabBarIconStyle: {
            // Full-width icon container (as in bottom-tabs v6, where it was `flex: 1`) so wide
            // product logos keep their height; v7 gives the container a fixed width by default.
            alignSelf: 'stretch',
            width: '100%',
          },
          tabBarIcon: ({ color, size }) => (
            <Flex alignItems="center" height={`${size}px`} width="full">
              <RemoteSVG
                color={color}
                fallback={
                  !productContent ? <Spinner size="sm" /> : <Circle bgColor={color} size={size} />
                }
                style={{ flex: 1 }}
                uri={productContent?.logoSpotOutline?.url ?? ''}
              />
            </Flex>
          ),
        })}
      />
      <BottomTab.Screen
        component={AttractionTicketsTab}
        initialParams={{
          screen: 'AttractionTickets',
          params: { productId: productContentKey },
        }}
        name="AttractionTicketsStack"
        options={({ navigation: $navigation }) => ({
          title: t<string>(
            entitlement ? 'app_product_tab_tickets_auth' : 'app_product_tab_tickets'
          ),
          tabBarButtonTestID: tID('TabAttractionTickets'),
          headerShown: false,
          tabBarIcon: ({ color }) => <QRTicket color={color} size={ICON_SIZE} />,
          tabBarButton: ({ children }) => (
            <TabBarButton
              analyticsEvent={Events.NavTickets}
              navigation={$navigation}
              routeParams={routeParams}
              screen="AttractionTicketsStack"
              testID={tID('TabAttractionTickets')}
            >
              {children}
            </TabBarButton>
          ),
          freezeOnBlur: true,
        })}
      />
      <BottomTab.Screen
        component={MenuScreen}
        name="Menu"
        options={({ navigation: $navigation }) => ({
          title: t<string>('app_product_tab_menu'),
          tabBarButtonTestID: tID('TabMore'),
          tabBarIcon: ({ color }) => (
            <Box p="1">
              <Menu color={color} size={20} />
              {unreadCount > 0 && (
                <Circle
                  bg="brand.active"
                  borderColor="white"
                  borderWidth="2"
                  position="absolute"
                  right="-1"
                  size="3.5"
                  top="-1"
                />
              )}
            </Box>
          ),
          tabBarButton: ({ children }) => (
            <TabBarButton
              analyticsEvent={Events.NavMore}
              navigation={$navigation}
              routeParams={routeParams}
              screen="Menu"
              testID={tID('TabMore')}
            >
              {children}
            </TabBarButton>
          ),
        })}
      />
    </BottomTab.Navigator>
  );
}

function TabBarButton({
  navigation,
  screen,
  routeParams,
  analyticsEvent,
  testID,
  children,
}: {
  children?: ReactNode;
  screen: keyof ProductTabParamList;
  routeParams: Record<string, string>;
  navigation: Pick<NavigationProp<ParamListBase>, 'dispatch'>;
  analyticsEvent: Events;
  testID?: string;
}) {
  return (
    <Pressable
      // Center the icon over the label. In bottom-tabs v7 the icon wrapper has a fixed width and
      // relies on the tab button's style (`alignItems: 'center'`), which this custom button does
      // not receive.
      alignItems="center"
      flex="1"
      testID={testID}
      onPress={() => {
        // Same action as `navigation.jumpTo`, whose v7 typing does not accept the extra
        // `productId` param this tab bar passes to every tab.
        navigation.dispatch(TabActions.jumpTo(screen, routeParams));
        Analytics.trackEvent(analyticsEvent, routeParams);
      }}
    >
      {children}
    </Pressable>
  );
}
